import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  Store, Package, ShoppingBag, BarChart3, Settings, Plus, 
  Menu, X, ChevronRight, LogOut, Home 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [store, setStore] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user?.user_type !== 'seller') {
      toast.error('Satıcı hesabı gerekli');
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      // Try to get store
      try {
        const storeRes = await axios.get(`${API}/my-store`);
        setStore(storeRes.data);
        
        // If store exists, get stats and products
        const [statsRes, productsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/seller/stats`),
          axios.get(`${API}/my-products`),
          axios.get(`${API}/seller/orders`)
        ]);
        setStats(statsRes.data);
        setProducts(productsRes.data);
        setOrders(ordersRes.data);
      } catch (e) {
        // No store yet
        setStore(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/seller/dashboard' },
    { icon: Package, label: 'Ürünlerim', path: '/seller/products' },
    { icon: ShoppingBag, label: 'Siparişler', path: '/seller/orders' },
    { icon: Store, label: 'Mağaza Ayarları', path: '/seller/store-settings' },
    { icon: MessageSquare, label: 'Mesajlar', path: '/seller/messages' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  // Show create store form if no store
  if (!store) {
    return <CreateStoreForm onSuccess={fetchData} />;
  }

  const isMainDashboard = location.pathname === '/seller/dashboard';

  return (
    <div className="min-h-screen bg-slate-100" data-testid="seller-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => setSidebarOpen(true)}>
          <Menu className="h-6 w-6" />
        </button>
        <h1 className="font-bold">{store.name}</h1>
        <Link to="/">
          <Home className="h-6 w-6" />
        </Link>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-200
          lg:translate-x-0 lg:static lg:inset-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Mobile Close */}
          <div className="lg:hidden flex justify-end p-4">
            <button onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Store Info */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Store className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900">{store.name}</h2>
                <Badge variant={store.is_verified ? 'default' : 'secondary'} className="text-xs">
                  {store.is_verified ? 'Doğrulanmış' : 'Onay Bekliyor'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Menu */}
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  location.pathname === item.path
                    ? 'bg-orange-100 text-orange-600'
                    : 'hover:bg-slate-100 text-slate-600'
                }`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
            <Link to="/" className="flex items-center gap-3 px-4 py-2 text-slate-600 hover:text-slate-900">
              <Home className="h-5 w-5" />
              Siteye Git
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-red-600 hover:text-red-700 w-full"
            >
              <LogOut className="h-5 w-5" />
              Çıkış Yap
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {isMainDashboard ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Toplam Ürün</p>
                        <p className="text-2xl font-bold text-slate-900">{stats?.total_products || 0}</p>
                      </div>
                      <Package className="h-8 w-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Toplam Sipariş</p>
                        <p className="text-2xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
                      </div>
                      <ShoppingBag className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Bekleyen</p>
                        <p className="text-2xl font-bold text-slate-900">{stats?.pending_orders || 0}</p>
                      </div>
                      <Badge className="bg-yellow-500">{stats?.pending_orders || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Toplam Gelir</p>
                        <p className="text-2xl font-bold text-green-600">{formatPrice(stats?.total_revenue || 0)}</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Son Ürünler
                      <Link to="/seller/products">
                        <Button variant="ghost" size="sm">
                          Tümü <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {products.length > 0 ? (
                      <div className="space-y-3">
                        {products.slice(0, 5).map((product) => (
                          <div key={product.id} className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Package className="w-full h-full p-2 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{product.name}</p>
                              <p className="text-sm text-orange-600">{formatPrice(product.price)}</p>
                            </div>
                            <Badge variant="secondary">{product.stock} adet</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Package className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>Henüz ürün yok</p>
                      </div>
                    )}
                    <Link to="/seller/products/new" className="block mt-4">
                      <Button className="w-full bg-orange-500 hover:bg-orange-600">
                        <Plus className="h-4 w-4 mr-2" />
                        Yeni Ürün Ekle
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Son Siparişler
                      <Link to="/seller/orders">
                        <Button variant="ghost" size="sm">
                          Tümü <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {orders.length > 0 ? (
                      <div className="space-y-3">
                        {orders.slice(0, 5).map((order) => (
                          <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium">#{order.id.slice(0, 8).toUpperCase()}</p>
                              <p className="text-sm text-slate-500">
                                {new Date(order.created_at).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-orange-600">{formatPrice(order.total_amount)}</p>
                              <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                                {order.status === 'pending' ? 'Bekliyor' : 
                                 order.status === 'confirmed' ? 'Onaylandı' : order.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <ShoppingBag className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>Henüz sipariş yok</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Commission Info */}
              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold mb-1">Komisyon Bilgisi</h3>
                      <p className="text-orange-100">
                        Her satıştan %10 platform komisyonu alınmaktadır.
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">%10</p>
                      <p className="text-orange-100 text-sm">Komisyon Oranı</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Outlet context={{ store, products, orders, fetchData }} />
          )}
        </main>
      </div>
    </div>
  );
};

// Create Store Form Component
const CreateStoreForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Mağaza adı gerekli');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/stores`, formData);
      toast.success('Mağaza oluşturuldu!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mağaza oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="create-store-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Store className="h-8 w-8 text-orange-600" />
          </div>
          <CardTitle className="text-2xl">Mağazanızı Oluşturun</CardTitle>
          <p className="text-slate-500">
            Ürünlerinizi satmaya başlamak için mağaza bilgilerinizi girin
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mağaza Adı *</label>
              <input
                type="text"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Yedek Parça Dünyası"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="store-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Açıklama</label>
              <textarea
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Mağazanız hakkında kısa bilgi..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="store-description-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefon</label>
              <input
                type="tel"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="05XX XXX XX XX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="store-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Adres</label>
              <input
                type="text"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="İstanbul, Türkiye"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="store-address-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 h-12"
              disabled={loading}
              data-testid="create-store-btn"
            >
              {loading ? 'Oluşturuluyor...' : 'Mağazamı Oluştur'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerDashboard;
